import { useParams } from 'react-router-dom';
import { aicaAIProfileBanners, plusWhite, poweredByAICAWhite } from '../../../../../../assets/hostedassets';
import Typewriter from '../../../../../../common/Typewriter/Typewriter';
import { INVESTOR_OPEN_COMPANY_WEBSITE } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { getTimeStamp } from '../../../../../../utils/dateUtils';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getHashFromId, truncateByCharacters, validateCIN } from '../../../../../../utils/utils';
import styles from './Details.module.scss';
import { useEffect, useState } from 'react';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import AicaOverview from '../../../../../../common/AicaOverview/AicaOverview';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { setDealDrawer } from '../../../../../../store/investor/action';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../../../app/hooks';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { getFormerEntityData } from '../../../../../../store/investor/apiHelpers';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { getCompanyLogo, getFaviconUrl } from '../../../../../../utils/urlMetadata';
import AboutCompanyOverview from './AboutCompanyOverview/AboutCompanyOverview';
import AIGeneratedBadge from '../../../../../../common/AIGeneratedBadge/AIGeneratedBadge';
import AskAicaV2 from '../AskAicaV2/AskAicaV2';
import AskAicaQuestionBadge from '../../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';

interface FormerEntity {
    name: string;
    date: string;
}

const foundedIcon = 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/founded.svg';
const employeesIcon = 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/employees.svg';
const fundingIcon = 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/funding.svg';
const location = 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/location.svg';
const linkedin = 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/linkedIn-white.svg';
const ctaRightWhite = 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/ctaRightWhite.svg';

function Details({ overviewData, accessType, selectedCompany, isPDFReport, overviewDataV2 }) {
    const {
        sectorInfo,
        overview,
        businessModel,
        socialInfo,
        foundationDate,
        headquarterLocation,
        fundingInfo,
        employeeData,
        cinNumber,
        panNumber,
        gstin,
    } = overviewData;

    const params = useParams();
    const [bannerLoaded, setBannerLoaded] = useState(false);
    const [formerEntityData, setFormerEntityData] = useState<FormerEntity[]>([]);
    const showSectorSubsector = !!(sectorInfo?.sector || sectorInfo?.subSector);
    const user = useAppSelector((state) => state.user.userData);
    const showAskAica = useAppSelector((state) => state.dashboard.dashboardReducer.config?.tabConfigs?.overview?.showAskAica);
    const isValidUser = [USER_TYPE.INVESTOR, USER_TYPE.ADMIN_TEAM].includes(user?.accountType);
    const showOverview = !!overview;
    const invalidSectors = [null, '', 'Others'];
    const dispatch = useDispatch();

    const bannerImgSrc = getImageForId(params.id || '');

    // Array of video URLs to choose from randomly
    const videoUrls = [
        'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/mp4/companyBanner1.mp4',
        'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/mp4/companyBanner2.mp4',
        'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/mp4/companyBanner.mp4',
    ];

    // Get random video URL
    const getRandomVideoUrl = () => {
        const randomIndex = Math.floor(Math.random() * videoUrls.length);
        return videoUrls[randomIndex];
    };

    const [selectedVideoUrl] = useState(() => getRandomVideoUrl());

    const additionalDetails = (cinNumber && cinNumber !== '') || (panNumber && panNumber !== '') || gstin?.length > 0;
    const bodyHasData = !!(
        sectorInfo?.sector ||
        sectorInfo?.subSector ||
        !!overview ||
        !!businessModel ||
        !!headquarterLocation?.fullAddress ||
        additionalDetails
    );

    useEffect(() => {
        if (bannerImgSrc) {
            const img = new Image();
            img.src = bannerImgSrc;
            img.onload = () => setBannerLoaded(true);
        }
    }, [bannerImgSrc]);

    useEffect(() => {
        fetchFormerEntityData();
    }, [params.id]);

    const fetchFormerEntityData = () => {
        getFormerEntityData(
            { organizationId: params.id || '' },
            {
                onSuccess: (data) => {
                    setFormerEntityData(data);
                },
            },
        );
    };

    const onWebsiteClick = () => {
        Mixpanel.track(INVESTOR_OPEN_COMPANY_WEBSITE, {
            Timestamp: getTimeStamp(),
            AICASection: accessType,
        });
    };

    function formatDate(inputDate: string): string {
        const [day, month, year] = inputDate.split('-');
        const date = new Date(`${year}-${month}-${day}`);

        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ');
    }

    function getImageForId(id: string): string {
        const hash = getHashFromId(id);
        const index = hash % aicaAIProfileBanners.length;
        return aicaAIProfileBanners[index];
    }

    const getFirstGstIn = (gstinList) => {
        const [stateName, gstinValue] = Object.entries(gstinList[0])[0] as [string, string];
        return gstinValue;
    };

    const getToolTipContent = (gstinList) => {
        return (
            <div>
                {gstinList?.slice(1).map((gstinObj) => {
                    const [stateName, gstinValue] = Object.entries(gstinObj)[0] as [string, string];
                    return (
                        <div className={styles.GstTooltip} key={`${stateName}-${gstinValue}`}>
                            {stateName}:<span className={styles.Bold}> {gstinValue} </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const getFormerEntityTooltipContent = (formerEntities: FormerEntity[]) => {
        return (
            <div>
                {formerEntities?.map((entity, index) => (
                    <div className={styles.FormerEntityTooltip} key={`${entity.name}-${index}`}>
                        <div className={styles.FormerEntityName}>
                            <span>{entity.name}</span>
                        </div>
                        {entity.date && <div className={styles.FormerEntityDate}>(Till {dayjs(entity.date).format('DD MMM YYYY')})</div>}
                        {index !== formerEntities.length - 1 && <div className={styles.FormerEntitySeparator}></div>}
                    </div>
                ))}
            </div>
        );
    };

    const addSocials = (buttonName: String) => {
        if (!isValidUser) return;
        return (
            <div
                className={clsx(styles.Pill, styles.PillWithHover)}
                style={{ cursor: 'pointer', columnGap: '5px' }}
                onClick={() => {
                    dispatch(
                        setDealDrawer({
                            open: true,
                            drawerLabel: 'Edit Profile',
                            drawerSource: 'Debt Products',
                            selectedStep: 2,
                        }),
                    );
                }}
            >
                <img src={plusWhite} alt="" height={'13.33px'} />
                {buttonName}
            </div>
        );
    };

    const renderTopSection = () => (
        <div
            className={styles.Top}
            style={{
                background: isPDFReport ? 'linear-gradient(127deg, rgba(0,0,0,1) 0%, rgba(96,33,179,1) 100%)' : 'none', // Remove background when using video
                paddingBottom: !showAskAica ? '34px' : '',
            }}
        >
            {!isPDFReport && (
                <video
                    className={styles.BackgroundVideo}
                    style={{ height: '200px' }}
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={bannerLoaded ? bannerImgSrc : undefined}
                >
                    <source src={selectedVideoUrl} type="video/mp4" />
                </video>
            )}
            <div className={styles.TopSection}>
                <div className={styles.CompanyLogo}>
                    {(() => {
                        const companyLogo = selectedCompany?.logo;
                        const websiteUrl = socialInfo?.websiteUrl;
                        const logoFromWebsite = websiteUrl && getCompanyLogo(websiteUrl);
                        const faviconFromWebsite = websiteUrl && getFaviconUrl(websiteUrl);
                        const logoSrc = companyLogo || logoFromWebsite || faviconFromWebsite;

                        return logoSrc ? (
                            <img
                                src={logoSrc}
                                alt="Company Logo"
                                width={'100%'}
                                style={{
                                    borderRadius: '8px',
                                }}
                                onError={(e) => {
                                    // If image fails to load, replace with placeholder
                                    e.currentTarget.style.display = 'none';
                                    const parent = e.currentTarget.parentElement;
                                    if (parent) {
                                        const placeholder = document.createElement('div');
                                        placeholder.className = styles.PlaceholderImg;
                                        placeholder.textContent = selectedCompany?.orgName?.charAt(0) || '';
                                        parent.appendChild(placeholder);
                                    }
                                }}
                            />
                        ) : (
                            <div className={styles.PlaceholderImg}>{selectedCompany?.orgName?.charAt(0) || ''}</div>
                        );
                    })()}
                </div>
                <div className={styles.TopSectionRight}>
                    <div className={styles.Title}>
                        <Typewriter text={selectedCompany?.orgName} delay={20} />
                    </div>
                    <div className={styles.Badges}>
                        {!!foundationDate && (
                            <div className={styles.Pill}>
                                <img src={foundedIcon} alt="" height={'12px'} />
                                Founded {formatDate(foundationDate)}
                            </div>
                        )}
                        {!!employeeData?.currentEmployeeRange && (
                            <div className={styles.Pill}>
                                <img src={employeesIcon} alt="" height={'12px'} />
                                {employeeData?.currentEmployeeRange} Employees
                            </div>
                        )}

                        {!!fundingInfo?.stage && (
                            <div className={styles.Pill}>
                                <img src={fundingIcon} alt="" height={'12px'} />
                                {fundingInfo?.stage}
                            </div>
                        )}
                        {!!(headquarterLocation?.city || headquarterLocation?.state) && (
                            <div className={styles.Pill}>
                                <img src={location} alt="" height={'12px'} />
                                {headquarterLocation?.city}
                                {headquarterLocation?.city && headquarterLocation?.state && ', '}
                                {headquarterLocation?.state}
                            </div>
                        )}
                        {!!socialInfo?.websiteUrl ? (
                            <a
                                className={clsx(styles.Pill, styles.PillWithHover)}
                                href={
                                    socialInfo?.websiteUrl?.includes('https://') || socialInfo?.websiteUrl?.includes('http://')
                                        ? socialInfo?.websiteUrl
                                        : `https://${socialInfo.websiteUrl}`
                                }
                                target="_blank"
                                onClick={onWebsiteClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <img src={ctaRightWhite} alt="" height={'12px'} />
                                Website
                            </a>
                        ) : (
                            addSocials('Add Website')
                        )}
                        {!!socialInfo?.userInputLinkedin ? (
                            <a
                                className={clsx(styles.Pill, styles.PillWithHover)}
                                href={
                                    socialInfo?.userInputLinkedin?.includes('https://') ||
                                    socialInfo?.userInputLinkedin?.includes('http://')
                                        ? socialInfo?.userInputLinkedin
                                        : `https://${socialInfo.userInputLinkedin}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ cursor: 'pointer' }}
                            >
                                <img src={linkedin} alt="" height={'12px'} style={{ marginTop: '2px' }} />
                                LinkedIn
                            </a>
                        ) : (
                            addSocials('Add Linkedin')
                        )}
                    </div>
                </div>
            </div>

            <img className={styles.poweredByAica} src={poweredByAICAWhite} alt="powered by aica" height="18px" />

            {/* Ask AICA Floating Bar */}
            {!isPDFReport && <AskAicaV2 selectedCompany={selectedCompany} overviewData={overviewData} />}
        </div>
    );

    const renderBasicDetails = () => {
        // Check if there's any data to show in AboutCompanyOverview
        const hasAboutCompanyData = !!(
            overviewDataV2?.overview?.overviewContent ||
            overview ||
            overviewDataV2?.sectorDetails?.sector ||
            overviewDataV2?.sectorDetails?.subSector ||
            sectorInfo?.sector ||
            sectorInfo?.subSector
        );

        return (
            <>
                {/* Only render AboutCompanyOverview section if data is available */}
                {hasAboutCompanyData && (
                    <div className={styles.BottomWrap}>
                        <div className={styles.Header}>
                            <div className={styles.Heading}>About the Company</div>
                            <div className={styles.Right}>
                                {!isPDFReport && (
                                    <AskAicaQuestionBadge
                                        question="Summarize the company profile"
                                        prompt="Summarize the company profile in a few sentences"
                                    />
                                )}
                                <AIGeneratedBadge />
                            </div>
                        </div>
                        <div className={styles.Bottom}>
                            <AboutCompanyOverview
                                overviewDataV2={overviewDataV2}
                                overview={overview}
                                sectorInfo={sectorInfo}
                                invalidSectors={invalidSectors}
                            />
                        </div>
                    </div>
                )}

                {/* Original table for additionalDetails */}
                <div className={styles.BottomWrap} style={{ marginBottom: '0' }}>
                    <div className={styles.Header}>
                        <div className={styles.Heading}>Basic Details</div>
                        <div className={styles.Right}></div>
                    </div>
                    <div className={styles.Bottom}>
                        {additionalDetails && (
                            <>
                                <div className={styles.Badges}>
                                    {panNumber && (
                                        <div className={styles.Pill}>
                                            <span className={styles.Label}>PAN:</span> {panNumber}
                                        </div>
                                    )}
                                    {cinNumber && (
                                        <div className={styles.Pill}>
                                            <span className={styles.Label}>{validateCIN(cinNumber) ? 'CIN' : 'LLPIN'}:</span> {cinNumber}
                                        </div>
                                    )}
                                    {gstin?.length > 0 && (
                                        <div className={styles.Pill}>
                                            <span className={styles.Label}>GSTIN:</span>{' '}
                                            <>
                                                {isPDFReport ? (
                                                    <span
                                                        style={{
                                                            wordWrap: 'break-word',
                                                            whiteSpace: 'normal',
                                                            maxWidth: '100%',
                                                            display: 'inline-block',
                                                        }}
                                                    >
                                                        {gstin.map((gst) => Object.values(gst)[0]).join(', ')}
                                                    </span>
                                                ) : (
                                                    <>
                                                        {getFirstGstIn(gstin)}
                                                        {gstin?.length > 1 && (
                                                            <Tooltip
                                                                content={getToolTipContent(gstin)}
                                                                direction="right"
                                                                height="5.625rem"
                                                                isOverviewDetail={true}
                                                                contentCss={{ maxHeight: '5rem', overflowY: 'auto' }}
                                                            >
                                                                <span className={styles.ExtraGstin}> , +{gstin?.length - 1} more</span>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        </div>
                                    )}
                                    {!isEmpty(formerEntityData) && !isPDFReport && (
                                        <div className={styles.Pill}>
                                            <span className={styles.Label}>
                                                {formerEntityData.length === 1 ? 'Former Name:' : 'Former Names:'}
                                            </span>{' '}
                                            <>
                                                {formerEntityData[0]?.name && formerEntityData[0]?.name.length > 15 ? (
                                                    <Tooltip
                                                        content={formerEntityData[0]?.name}
                                                        direction="top"
                                                        isOverviewDetail={true}
                                                        fitContent={true}
                                                        style={{ left: '100px', maxWidth: '300px', top: '-60px' }}
                                                    >
                                                        <span>
                                                            {truncateByCharacters(formerEntityData[0]?.name, 15) +
                                                                ' (Till ' +
                                                                (formerEntityData[0]?.date
                                                                    ? dayjs(formerEntityData[0]?.date).format('DD MMM YYYY')
                                                                    : '') +
                                                                ')'}
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <span>
                                                        {truncateByCharacters(formerEntityData[0]?.name, 15) +
                                                            ' (Till ' +
                                                            (formerEntityData[0]?.date
                                                                ? dayjs(formerEntityData[0]?.date).format('DD MMM YYYY')
                                                                : '') +
                                                            ')'}
                                                    </span>
                                                )}
                                                {formerEntityData?.length > 1 && (
                                                    <Tooltip
                                                        content={getFormerEntityTooltipContent(formerEntityData)}
                                                        direction="top"
                                                        hideDelay={500}
                                                        isOverviewDetail={true}
                                                        style={{ left: '50px', maxWidth: '300px', top: '-150px' }}
                                                        contentCss={{ maxHeight: '8rem', overflowY: 'auto' }}
                                                    >
                                                        <span className={styles.ExtraGstin}> , +{formerEntityData?.length - 1} more</span>
                                                    </Tooltip>
                                                )}
                                            </>
                                        </div>
                                    )}
                                </div>

                                {/* Address as a separate section */}
                                {headquarterLocation?.fullAddress && (
                                    <>
                                        <div className={styles.Badges}>
                                            {isPDFReport ? (
                                                <div
                                                    className={`${styles.Pill} ${styles.AddressPill}`}
                                                    style={{
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        maxWidth: '100%',
                                                        display: 'inline-block',
                                                    }}
                                                >
                                                    {headquarterLocation?.fullAddress}
                                                </div>
                                            ) : (
                                                <Tooltip
                                                    content={headquarterLocation?.fullAddress}
                                                    direction="top"
                                                    fitContent={true}
                                                    isOverviewDetail={true}
                                                    style={{ top: '-60px', left: '350px' }}
                                                >
                                                    <div className={`${styles.Pill} ${styles.AddressPill}`}>
                                                        {headquarterLocation?.fullAddress}
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    };

    const render = (
        <div className={styles.Details} style={{ position: 'relative' }}>
            {renderTopSection()}
            <AicaOverview AicaOverviewBodyStyle={{ border: '1px solid #e7e7e7' }} style={{ marginBottom: '24px' }} module="overview" />
            {bodyHasData && renderBasicDetails()}
        </div>
    );

    if (isPDFReport)
        return reportSectionsToShow?.includes(REPORT_SECTION_NAMES.OVERVIEW) ? (
            <>
                <A4Page sectionName={REPORT_SECTION_NAMES.OVERVIEW}>
                    <div className={clsx(styles.Details, styles.PDFReport)}>
                        {renderTopSection()}
                        <AicaOverview style={{ marginTop: '0px', marginBottom: '24px' }} module={'overview'} isPDFReport={isPDFReport} />
                        {/* <>{renderBasicDetails()}</> */}
                    </div>
                </A4Page>
                {bodyHasData && (
                    <A4Page sectionName={REPORT_SECTION_NAMES.OVERVIEW}>
                        <div className={clsx(styles.Details, styles.PDFReport)}>{renderBasicDetails()}</div>
                    </A4Page>
                )}
            </>
        ) : (
            <></>
        );
    else return render;
}

export default Details;
